import { supabaseClient } from '../config/supabase';

export interface ErrorContext {
  action?: string;
  userId?: string;
  [key: string]: any; // Pozwala na dowolne dodatkowe pola
}

/**
 * Serwis do monitorowania i logowania zdarzeń w aplikacji
 */
export class MonitoringService {
  /**
   * Śledzi zdarzenie w aplikacji
   * @param eventName Nazwa zdarzenia
   * @param eventData Dodatkowe dane zdarzenia
   */
  trackEvent(eventName: string, eventData?: Record<string, any>): void {
    try {
      // Logowanie do konsoli w środowisku deweloperskim
      if (process.env.NODE_ENV === 'development') {
        console.log(`EVENT: ${eventName}`, eventData || {});
      }
      
      // Tutaj można dodać integrację z narzędziami analitycznymi
      // np. Google Analytics, Mixpanel, itp.
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  }

  /**
   * Śledzi błędy w aplikacji
   * @param error Obiekt błędu
   * @param errorData Dodatkowe informacje o błędzie
   */
  trackError(error: Error, errorData?: Record<string, any>): void {
    try {
      // Logowanie do konsoli w środowisku deweloperskim
      if (process.env.NODE_ENV === 'development') {
        console.error(`ERROR: ${error.message}`, {
          stack: error.stack,
          ...errorData,
        });
      }
      
      // Tutaj można dodać integrację z narzędziami do śledzenia błędów
      // np. Sentry, LogRocket, itp.
    } catch (e) {
      console.error('Error while tracking error:', e);
    }
  }

  /**
   * Śledzi czas trwania operacji
   * @param operationName Nazwa operacji
   * @param callback Funkcja do wykonania i zmierzenia czasu
   * @returns Wynik funkcji callback
   */
  async trackTiming<T>(operationName: string, callback: () => Promise<T>): Promise<T> {
    const startTime = performance.now();
    try {
      const result = await callback();
      const duration = performance.now() - startTime;
      
      this.trackEvent('TIMING', {
        operation: operationName,
        durationMs: Math.round(duration),
      });
      
      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      
      this.trackEvent('TIMING_ERROR', {
        operation: operationName,
        durationMs: Math.round(duration),
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      
      throw error;
    }
  }

  /**
   * Śledzi czas trwania operacji synchronicznej
   * @param operationName Nazwa operacji
   * @param callback Funkcja do wykonania i zmierzenia czasu
   * @returns Wynik funkcji callback
   */
  trackTimingSync<T>(operationName: string, callback: () => T): T {
    const startTime = performance.now();
    try {
      const result = callback();
      const duration = performance.now() - startTime;
      
      this.trackEvent('TIMING', {
        operation: operationName,
        durationMs: Math.round(duration),
      });
      
      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      
      this.trackEvent('TIMING_ERROR', {
        operation: operationName,
        durationMs: Math.round(duration),
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      
      throw error;
    }
  }

  /**
   * Śledzi nawigację użytkownika
   * @param path Ścieżka, do której nawiguje użytkownik
   * @param params Parametry nawigacji
   */
  trackNavigation(path: string, params?: Record<string, any>): void {
    this.trackEvent('NAVIGATION', {
      path,
      ...params,
    });
  }

  /**
   * Śledzi interakcje użytkownika
   * @param actionType Typ akcji (np. click, submit, hover)
   * @param elementId Identyfikator elementu
   * @param additionalData Dodatkowe dane o interakcji
   */
  trackUserInteraction(
    actionType: 'click' | 'submit' | 'hover' | 'scroll' | 'keypress' | 'other',
    elementId: string,
    additionalData?: Record<string, any>
  ): void {
    this.trackEvent('USER_INTERACTION', {
      action: actionType,
      element: elementId,
      ...additionalData,
    });
  }

  /**
   * Śledzi zachowanie użytkownika
   * @param actionType Typ akcji (np. sign_in_attempt, sign_out_attempt)
   * @param additionalData Dodatkowe dane o interakcji
   */
  trackUserBehavior(
    actionType: string,
    additionalData?: Record<string, any>
  ): void {
    this.trackEvent('USER_BEHAVIOR', {
      action: actionType,
      ...additionalData,
    });
  }

  async trackBusinessMetric(metricName: string, value: number, userId?: string) {
    try {
      const { error } = await supabaseClient
        .from('metrics')
        .insert([{
          metric_name: metricName,
          value,
          user_id: userId,
          timestamp: new Date().toISOString()
        }]);

      if (error) throw error;
    } catch (err) {
      console.error('Failed to track business metric:', err);
    }
  }

  extractInterests(quizAnswers: any): string[] {
    const interests: string[] = [];

    if (quizAnswers?.interests) {
      interests.push(...quizAnswers.interests);
    }

    if (quizAnswers?.skills) {
      interests.push(...quizAnswers.skills);
    }

    return [...new Set(interests)].filter(Boolean);
  }
}

export const monitoringService = new MonitoringService();

export const trackError = async (error: Error | any, context?: ErrorContext) => {
  try {
    console.error('Error tracked:', {
      error_name: error.name || 'UnknownError',
      error_message: error.message || String(error),
      error_stack: error.stack,
      context,
      timestamp: new Date().toISOString()
    });
    
    // Zamiast zapisywać w Supabase, tylko logujemy do konsoli i możemy wysłać telemetrię
    // Możemy dodać tutaj inne sposoby logowania błędów, np. localStorage jako fallback
    
    // Zapisuj błędy do localStorage jako backup
    const errors = JSON.parse(localStorage.getItem('error_logs') || '[]');
    errors.push({
      error_name: error.name || 'UnknownError',
      error_message: error.message || String(error),
      context,
      timestamp: new Date().toISOString()
    });
    
    // Zachowaj tylko ostatnie 50 błędów
    if (errors.length > 50) {
      errors.splice(0, errors.length - 50);
    }
    
    localStorage.setItem('error_logs', JSON.stringify(errors));
    
    return true;
  } catch (trackingError) {
    console.error('Failed to track error:', trackingError);
    return false;
  }
}
